import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import RulerVerticalIcon from '../icons/RulerVerticalIcon';
import ScaleIcon from '../icons/ScaleIcon';
import DistanceIcon from '../icons/DistanceIcon';
import ArmIcon from '../icons/ArmIcon';
import HandIcon from '../icons/HandIcon';
import DashboardIcon from '../icons/DashboardIcon';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Tooltip from '../atoms/Tooltip';
import Player from '../types/Player';
import Position from '../types/Position';
import HighSchool from '../types/HighSchool';
import HighSchoolCombine from '../types/HighSchoolCombine';
import { inchesToFeetAndInches } from '../services/converter';
import { formatDate, getMostRecentDataByProperty } from '../services/player';
import { COLOR_BLACK, COLOR_BLUE, COLOR_ORANGE, COLOR_WHITE } from '../styles/colors';
import NotAvailable from '../atoms/NotAvailable';
import InfoIcon from '../icons/InfoIcon';
import Grid from '@material-ui/core/Grid';

interface HighSchoolBiometricsProps {
  className?: string;
  player?: Player;
  primarySchool?: HighSchool;
  selectedPosition?: Position;
}

const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    fontWeight: 700,
  },
  headerIcon: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 44,
    height: 44,
    marginRight: theme.spacing(1.5),
    borderRadius: '50%',
    border: `2px solid ${COLOR_BLACK}`,
    backgroundColor: COLOR_BLUE,
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_WHITE,
    
    [theme.breakpoints.down('md')]: {
      width: 34,
      height: 34,
      fontSize: theme.typography.pxToRem(12),
    },
  },
  gridContainer: {
    alignItems: 'center',
  },
  rowContent: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    
    [theme.breakpoints.down('md')]: {
      display: 'inline-flex',
    },
  },
  infoIcon: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(1),
  },
  progressLineWrapper: {
    width: '100%',
  },
  progressLine: {
    maxWidth: 350,
    padding: 0,
    marginLeft: 'auto',
    
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
  },
  progressLineBar: {
    backgroundColor: COLOR_BLUE,
  },
}), { name: 'HighSchoolBiometrics' });

export default function HighSchoolBiometrics(props: HighSchoolBiometricsProps) {
  const { className, player, selectedPosition } = props;
  const classes = useStyles();

  const primarySchool = ((player && player.highSchools) || [])
    .find((highSchool: HighSchool) => highSchool && highSchool.isPrimary);
  
  const heightPercentile: any = primarySchool && primarySchool.heightPerc && (primarySchool.heightPerc
    .find((heightPerc: any) => heightPerc && heightPerc.code === selectedPosition) || {} as any).percentile;
  const weightPercentile: any = primarySchool && primarySchool.weightPerc && (primarySchool.weightPerc
    .find((weightPerc: any) => weightPerc && weightPerc.code === selectedPosition) || {} as any).percentile;

  const highSchoolCombines: HighSchoolCombine[] = (player && player.hsCombines) || [];
  const combineHeight = getMostRecentDataByProperty(highSchoolCombines, 'height');
  const combineHeightsPercData = getMostRecentDataByProperty(highSchoolCombines, 'heightPerc');
  const combineHeightPerc = combineHeightsPercData?.find((hp: any) => hp.code === selectedPosition)?.percentile;
  const combineWeight = getMostRecentDataByProperty(highSchoolCombines, 'weight');
  const combineWeightsPerc = getMostRecentDataByProperty(highSchoolCombines, 'weightPerc');
  const combineWeightPerc = combineWeightsPerc?.find((wp: any) => wp.code === selectedPosition)?.percentile;

  const combineType = getMostRecentDataByProperty(highSchoolCombines, 'type');
  const teamCamp = getMostRecentDataByProperty(highSchoolCombines, 'teamCamp');
  const combineDate = getMostRecentDataByProperty(highSchoolCombines, 'combineDate');
  const updatedAt = getMostRecentDataByProperty(highSchoolCombines, 'updatedAt');
  const wingspan = getMostRecentDataByProperty(highSchoolCombines, 'wingspan');
  const wingspanPerc = getMostRecentDataByProperty(highSchoolCombines, 'wingspanPerc');
  const arm = getMostRecentDataByProperty(highSchoolCombines, 'arm');
  const armPerc = getMostRecentDataByProperty(highSchoolCombines, 'armPerc');
  const hand = getMostRecentDataByProperty(highSchoolCombines, 'hand');
  const handPerc = getMostRecentDataByProperty(highSchoolCombines, 'handPerc');

  const wingspanPercentile = wingspan ? wingspanPerc?.find((wp: any) => wp.code === selectedPosition)?.percentile : undefined;
  const armPercentile = arm ? armPerc?.find((ap: any) => ap.code === selectedPosition)?.percentile : undefined;
  const handPercentile = hand ? handPerc?.find((hp: any) => hp.code === selectedPosition)?.percentile : undefined;

  function calculateOverallHSBiometricRating() {
    const hasHeight = typeof primarySchool?.height === 'number' && typeof heightPercentile === 'number';
    const hasWeight = typeof primarySchool?.weight === 'number' && typeof weightPercentile === 'number';

    if (!hasHeight || !hasWeight) {
      return undefined;
    }

    const optionalPercentiles = [wingspanPercentile, armPercentile, handPercentile]
      .filter((value): value is number => typeof value === 'number');

    if (optionalPercentiles.length < 2) {
      return undefined;
    }

    const percentileValues = [heightPercentile, weightPercentile, ...optionalPercentiles];
    const average = percentileValues.reduce((sum, value) => sum + value, 0) / percentileValues.length;

    return Math.round(average * 10) / 10;
  }
  

  const data = {
    playerId: player?.id,
    type: combineType,
    isCombineHeight: !!combineHeight,
    isCombineWeight: !!combineWeight,
    height: combineHeight || primarySchool?.height,
    heightPercentile: combineHeightPerc || heightPercentile,
    weight: combineWeight || primarySchool?.weight,
    weightPercentile: combineWeightPerc || weightPercentile,
    wingspan: wingspan,
    wingspanPercentile,
    arm: arm,
    armPercentile,
    hand: hand,
    handPercentile,
    overallHSBiometricRating: calculateOverallHSBiometricRating(),
  }

  function renderBiometricRow(value: React.ReactNode, percentile?: number, columnName?: string) {
    const renderHighSchoolJsonSource = () => (
      <div>
        <div>Source: High School JSON Data</div>
        <div>Note: Combine data unavailable, using high school profile values.</div>
      </div>
    );

    const ToolTipText = () => {
      switch (columnName) {
        case 'height':
          if (data?.isCombineHeight) {
            return (
              <div>
                <div>Source: {combineType || teamCamp || 'N/A'}</div>
                <div>Source Date: {formatDate(combineDate) || 'N/A'}</div>
                <div>Updated Date: {formatDate(updatedAt) || 'N/A'}</div>
              </div>
            )
          }
          return renderHighSchoolJsonSource();
        case 'weight':
          if (data?.isCombineWeight) {
            return (
              <div>
                <div>Source: {combineType || teamCamp || 'N/A'}</div>
                <div>Source Date: {formatDate(combineDate) || 'N/A'}</div>
                <div>Updated Date: {formatDate(updatedAt) || 'N/A'}</div>
              </div>
            )
          }
          return renderHighSchoolJsonSource();
        default:
          return <div>
            <div>Source: {combineType || teamCamp || 'N/A'}</div>
            <div>Source Date: {formatDate(combineDate) || 'N/A'}</div>
            <div>Updated Date: {formatDate(updatedAt) || 'N/A'}</div>
          </div>;

      }
    };
    return (
      <Grid container className={classes.gridContainer}>
        <Grid item lg={4} sm={5} xs={12}>
          <div className={classes.rowContent}>
            <span>{value ? <b>{value}</b> : <NotAvailable />}</span>

            <span>{value ? 
                <Tooltip title={<ToolTipText />} placement="top">
                  <InfoIcon className={classes.infoIcon} /> 
                </Tooltip>
              : null}</span>
          </div>
        </Grid>
        <Grid item lg xs>
          {(typeof percentile === 'number') && (
            <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
              <div className={classes.progressLineWrapper}>
                <ProgressLine
                  className={classes.progressLine}
                  progressBarClassName={classes.progressLineBar}
                  value={percentile}
                />
              </div>
            </Tooltip>
          )}
        </Grid>
      </Grid>
    );
  }

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      name={
        <div className={classes.header}>
          <div className={classes.headerIcon}>BIO</div>
          <span>High School Biometrics</span>
        </div>
      }
    >

      <ListItem
          icon={RulerVerticalIcon}
          title="Height:"
        >
          {renderBiometricRow(inchesToFeetAndInches(data?.height || 0), data.heightPercentile, 'height')}
        </ListItem>

      {/* Weight */}
        <ListItem
          icon={ScaleIcon}
          title="Weight:"
        >
          {renderBiometricRow(data.weight ? `${data.weight} lbs.` : '', data.weightPercentile, 'weight')}
        </ListItem>

      {/* Wingspan */}
      <ListItem
        icon={DistanceIcon}
        title="Wingspan:"
      >
        {renderBiometricRow(data.wingspan, data.wingspanPercentile)}
      </ListItem>

      {/* Arm */}
      <ListItem
        icon={ArmIcon}
        title="Arm:"
      >
        {renderBiometricRow(data.arm, data.armPercentile)}
      </ListItem>

      {/* Hand */}
      <ListItem
        icon={HandIcon}
        title="Hand:"
      >
        {renderBiometricRow(data.hand, data.handPercentile)}
      </ListItem>

      {/* Overall Rating */}
        <ListItem
          icon={DashboardIcon}
          title="Overall HS Biometric Rating:"
          extendWith={true}
        >
          {
            data.overallHSBiometricRating ?
              <>
                {(typeof data.overallHSBiometricRating === 'number') && (
                  <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                    <div className={classes.progressLineWrapper}>
                      <ProgressLine
                        className={classes.progressLine}
                        progressBarClassName={classes.progressLineBar}
                        value={data.overallHSBiometricRating}
                      />
                    </div>
                  </Tooltip>
                )}
              </>
            : <NotAvailable />
          }
        </ListItem>
      {/* ): ''} */}
    </NamedBox>
  );
}

